import { Button, Chip, Drawer, Group, MultiSelect, NumberInput, Select, Stack, Switch, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRates } from '@/api/catalog';
import { useGenerateKioskPin } from '@/api/kiosk';
import { useGroups, useMembers, useTransferOwnership, useUpdateMember, type Member } from '@/api/workspace';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { RateField } from '@/components/RateField';
import { ProfileFieldsSection } from './ProfileFieldsSection';
import { useApiError } from '@/hooks/useApiError';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;
type Day = (typeof DAYS)[number];

/** Mitglied bearbeiten: Rollen, Team-Manager-Bereich, Gruppen, Kapazität, Status, Sätze. */
export function MemberDrawer({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: members = [] } = useMembers(ctx.workspaceId);
  const { data: groups = [] } = useGroups(ctx.workspaceId);
  const { data: rates = [] } = useRates(ctx.workspaceId, ctx.isAdmin);
  const update = useUpdateMember(ctx.workspaceId);
  const transfer = useTransferOwnership(ctx.workspaceId);
  const generatePin = useGenerateKioskPin(ctx.workspaceId);
  const [generatedPin, setGeneratedPin] = useState<string | null>(null);
  const onError = useApiError();
  const [admin, setAdmin] = useState(false);
  const [teamManager, setTeamManager] = useState(false);
  const [managedMembers, setManagedMembers] = useState<string[]>([]);
  const [managedGroups, setManagedGroups] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [capacity, setCapacity] = useState<number | string>(8);
  const [days, setDays] = useState<string[]>([]);
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (member) {
      setGeneratedPin(null);
      setAdmin(member.roles.includes('ADMIN'));
      setTeamManager(member.roles.includes('TEAM_MANAGER'));
      setManagedMembers(member.managedMembershipIds);
      setManagedGroups(member.managedGroupIds);
      setGroupIds(member.groupIds);
      setCapacity(member.capacityMinutes / 60);
      setDays(member.workingDays);
      setActive(member.status !== 'INACTIVE');
    }
  }, [member]);

  if (!member) return <Drawer opened={false} onClose={onClose} />;
  const isOwner = member.roles.includes('OWNER');

  const save = () => {
    const roles = [...(isOwner ? (['OWNER'] as const) : []), ...(admin ? (['ADMIN'] as const) : []), ...(teamManager ? (['TEAM_MANAGER'] as const) : [])];
    update.mutate(
      {
        membershipId: member.membershipId,
        roles: [...roles],
        managedMembershipIds: managedMembers,
        managedGroupIds: managedGroups,
        groupIds,
        capacityMinutes: Math.round(Number(capacity) * 60),
        workingDays: days as Day[],
        status: member.status === 'INVITED' ? undefined : active ? 'ACTIVE' : 'INACTIVE',
      },
      { onSuccess: onClose, onError },
    );
  };

  return (
    <Drawer opened onClose={onClose} title={member.name} position="right" size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          {member.email}
        </Text>
        <Stack gap="xs">
          <Text fw={500} size="sm">
            {t('team.role')}
          </Text>
          {isOwner && <Text size="sm">{t('team.roles.OWNER')}</Text>}
          <Switch label={t('team.roles.ADMIN')} checked={admin || isOwner} disabled={isOwner} onChange={(e) => setAdmin(e.currentTarget.checked)} />
          {ctx.hasFeature('TEAM_MANAGERS') && (
            <Switch label={t('team.roles.TEAM_MANAGER')} checked={teamManager} onChange={(e) => setTeamManager(e.currentTarget.checked)} />
          )}
        </Stack>
        {teamManager && (
          <>
            <MultiSelect
              label={t('team.managesMembers')}
              data={members.filter((m) => m.membershipId !== member.membershipId).map((m) => ({ value: m.membershipId, label: m.name }))}
              value={managedMembers}
              onChange={setManagedMembers}
              searchable
            />
            <MultiSelect label={t('team.managesGroups')} data={groups.map((g) => ({ value: g.id, label: g.name }))} value={managedGroups} onChange={setManagedGroups} />
          </>
        )}
        <MultiSelect label={t('team.groups')} data={groups.map((g) => ({ value: g.id, label: g.name }))} value={groupIds} onChange={setGroupIds} />
        <NumberInput label={t('team.capacityPerDay')} min={0} max={24} decimalScale={2} suffix=" h" value={capacity} onChange={setCapacity} />
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            {t('team.workingDays')}
          </Text>
          <Chip.Group multiple value={days} onChange={setDays}>
            <Group gap={4}>
              {DAYS.map((d) => (
                <Chip key={d} value={d} size="xs">
                  {t(`days.${d}`)}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Stack>
        {member.userId && ctx.hasFeature('COST_RATES') && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('rates.costRate')}
            </Text>
            <RateField rates={rates} kind="COST" level="MEMBER" refId={member.userId} />
          </Stack>
        )}
        {member.userId && <ProfileFieldsSection userId={member.userId} />}
        {!isOwner && member.status !== 'INVITED' && (
          <Switch label={t('team.active')} checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        )}
        <Button onClick={save} loading={update.isPending}>
          {t('common.save')}
        </Button>
        {ctx.isAdmin && ctx.hasFeature('KIOSK') && member.status === 'ACTIVE' && member.userId && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('kiosk.pinTitle')}
            </Text>
            {generatedPin ? (
              <Text size="sm">
                {t('kiosk.generatedPin')} <b style={{ fontFamily: 'monospace', fontSize: 18 }}>{generatedPin}</b>
              </Text>
            ) : (
              <Button
                variant="light"
                size="xs"
                w="fit-content"
                loading={generatePin.isPending}
                onClick={() => generatePin.mutate({ userId: member.userId!, length: 4 }, { onSuccess: (r) => setGeneratedPin(r.pin), onError })}
              >
                {t('kiosk.generatePin')}
              </Button>
            )}
          </Stack>
        )}
        {ctx.isOwner && !isOwner && member.status === 'ACTIVE' && (
          <Select
            label={t('team.transferOwnership')}
            data={[{ value: 'yes', label: t('team.transferTo', { name: member.name }) }]}
            value={null}
            onChange={(v) => v && transfer.mutate(member.membershipId, { onSuccess: onClose, onError })}
          />
        )}
      </Stack>
    </Drawer>
  );
}
